import { TV2M_API_KEY_HEADER } from '@apm/contracts';
import { Tv2mHttpError } from '../interfaces.js';
/**
 * `fetch` 기반 `NetworkAdapter`.
 *
 * **`X-TV2M-Api-Key` 헤더를 여기서 붙인다.** 헤더 이름은 `@apm/contracts` 의
 * `TV2M_API_KEY_HEADER` 를 쓴다 — 서버의 `ApiKeyGuard` 와 같은 출처여야 한다 (절대 규칙 10).
 * 코어가 키를 들고 있지 않으므로, 운영에서 인증이 기기별 토큰으로 바뀌어도 고칠 곳은 여기다.
 *
 * `credentials` 를 붙이지 않는다. tv2m-server 는 `enableCors` 를 `credentials: false` 로
 * 띄운다(T1) — 인증이 헤더라 쿠키가 없다. 붙이면 CORS 가 조용히 막는다.
 *
 * **MVP 는 번들에 박히는 키다.** `VITE_TV2M_API_KEY` 는 브라우저로 내려가므로 비밀이 아니고,
 * 이것이 막는 것은 우연한 오용이지 결정한 공격자가 아니다 (`.env.example` 의 주석과 같은
 * 이야기다). 서버가 `ADMIN_PASSWORD` 를 단일 값으로 비교하는 것과 같은 수준이다.
 */
export class FetchNetworkAdapter {
    apiKey;
    constructor(apiKey) {
        this.apiKey = apiKey;
    }
    async get(url) {
        const res = await fetch(url, { headers: this.headers() });
        await this.assertOk(res, 'GET', url);
        return (await res.json());
    }
    /**
     * 본문이 없으면 `null` 을 준다. 202·204 에 `res.json()` 을 부르면 던지는데,
     * 그러면 이벤트 전송이 **성공했는데 실패한 것처럼 보인다** — 큐가 되돌려 무한히 재전송한다.
     */
    async post(url, body) {
        const res = await fetch(url, {
            method: 'POST',
            headers: { ...this.headers(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        await this.assertOk(res, 'POST', url);
        return (await readJsonOrNull(res)) ?? null;
    }
    async delete(url) {
        const res = await fetch(url, { method: 'DELETE', headers: this.headers() });
        await this.assertOk(res, 'DELETE', url);
    }
    headers() {
        return { Accept: 'application/json', [TV2M_API_KEY_HEADER]: this.apiKey };
    }
    /**
     * 실패를 `Tv2mHttpError` 로 던진다 — **상태 코드가 코어까지 가야 한다.**
     * heartbeat 의 404 는 재등록으로 회복할 수 있는 상태이고(T2), 문자열 메시지만 올리면
     * 그것을 네트워크 단절과 구분할 수 없다.
     *
     * 서버의 오류 본문(`{error:{code,message}}`)을 메시지에 담는다. 담지 않으면 400 이
     * 왔을 때 **어느 필드가 계약을 어겼는지**가 어디에도 남지 않는다.
     */
    async assertOk(res, method, url) {
        if (res.ok)
            return;
        const detail = await readErrorMessage(res);
        throw new Tv2mHttpError(res.status, `${method} ${url} 실패: ${String(res.status)}${detail ? ` — ${detail}` : ''}`);
    }
}
/** 본문이 비었거나 JSON 이 아니면 `null`. 던지지 않는다. */
async function readJsonOrNull(res) {
    const text = await res.text();
    if (text.trim() === '')
        return null;
    try {
        return JSON.parse(text);
    }
    catch {
        return null;
    }
}
async function readErrorMessage(res) {
    try {
        const body = await res.json();
        if (typeof body === 'object' && body !== null && 'error' in body) {
            const { error } = body;
            if (typeof error === 'object' && error !== null && 'message' in error) {
                const { message } = error;
                if (typeof message === 'string')
                    return message;
            }
        }
    }
    catch {
        // 본문이 JSON 이 아니어도 상태 코드는 이미 담겼다.
    }
    return null;
}
//# sourceMappingURL=fetch-network.js.map